import { FiMic } from 'react-icons/fi';

export const nuancedStanceData = {
  courseId: 'nuanced-stance',
  courseType: 'speaking-course',
  title: 'Nuanced Stance',
  icon: FiMic,
  overview: {
    impact: 'Qualify opinions so you sound measured and precise instead of all-or-nothing',
    focus: 'broadly / in part / with caveats — plus in principle, to some extent, on balance',
    uses: 'Speaking Part 3 discussion questions and any Part 1 opinion that would sound extreme if left absolute',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Why examiners reward a qualified opinion',
      content: `<p>A <strong>stance</strong> is your position. A <strong>nuanced stance</strong> is a position with limits: you say how far you agree, for whom the idea works, and what would change your mind. “I agree in principle, but the evidence is mixed” answers the question <em>and</em> shows critical thinking. “Yes, always” or “No, never” often sounds like a Band 6 script.</p>
<p>Lexical Resource and Fluency both benefit. Qualifiers such as <em>broadly</em>, <em>in part</em>, <em>to some extent</em>, and <em>with caveats</em> are precise academic English. They also buy you a second to choose an example, which keeps the turn moving. Nuance is not avoidance: you still take a side. You simply refuse a cartoon version of the side.</p>
<p>The test is simple. After your first sentence, can the examiner repeat your position in one line? If they can only say “he kind of maybe agrees,” you were vague. If they can say “she supports the ban in city centres but not in rural towns,” you were nuanced.</p>`,
      points: [
        'Answer first, then qualify — do not hide the opinion behind hedges.',
        'Name the limit: place, group, time, or condition.',
        'Give one example that matches the limit, not a second unrelated opinion.',
        'Avoid stacking softeners (“maybe sort of perhaps”) — that is vagueness, not stance.',
      ],
      examples: [
        { label: 'All or nothing', text: 'I completely agree. Homework is always useful.' },
        { label: 'Nuanced', text: 'I agree in principle, but the evidence is mixed — short practice helps, while three-hour tasks mainly cause stress.' },
      ],
    },
    {
      title: 'The core stance moves',
      content: `<p>Memorise four moves and recycle them. <strong>Broadly</strong> means you accept the general claim: “Broadly, I support later school start times.” <strong>In part</strong> means only some of the claim is true: “I agree in part: screens can harm sleep, but they also give rural students access to teachers.” <strong>In principle</strong> means you like the idea, yet practice is messy. <strong>With caveats</strong> names the conditions that must be true before you support the policy.</p>
<p>Add a second layer with <em>on balance</em> (after weighing both sides), <em>to some extent</em> (limited agreement), and <em>it depends on</em> plus a specific factor (age, income, city size). “It depends” alone is weak. “It depends on whether the city already has reliable buses” is a stance.</p>
<p>Do not use all four moves in one answer. One qualifier plus one caveat plus one example is the Band 7 pattern. Two examples and no clear side is a discussion, not a stance.</p>`,
      points: [
        'Broadly — general yes, details later.',
        'In part / to some extent — split the claim into true and false pieces.',
        'In principle — support the idea, doubt the implementation.',
        'With caveats / provided that — support only if a condition is met.',
        'On balance — you have weighed both sides and still choose one.',
      ],
      table: {
        headers: ['Move', 'Frame', 'Follow-up you must add'],
        rows: [
          ['Broadly', 'Broadly, I agree that…', 'One main reason, then a limit.'],
          ['In part', 'I agree in part: …, but…', 'Name the true piece and the false piece.'],
          ['In principle', 'I agree in principle, but…', 'Say what goes wrong in practice.'],
          ['Caveats', 'I would support this, with caveats.', 'State the condition (funding, age, place).'],
          ['On balance', 'On balance, I still think…', 'Show you heard the other side first.'],
        ],
      },
    },
    {
      title: 'Caveats that sound specific',
      content: `<p>A caveat is useful only if it is concrete. Weak: “I agree, with caveats.” Strong: “I would support congestion charges, with the caveat that low-income shift workers need cheaper night buses first.” The second sentence gives the examiner a picture and a condition they can follow.</p>
<p>Useful caveat types in IELTS topics: <em>who</em> (children vs adults, urban vs rural), <em>how much</em> (a ban vs a tax), <em>how soon</em> (pilot scheme vs nationwide law), and <em>what must come first</em> (training before automation). Pair the caveat with a contrast marker: <em>provided that</em>, <em>as long as</em>, <em>unless</em>, <em>except in</em>.</p>
<p>When the question is “Do you think X is good?”, a three-beat answer works under pressure: position (“Broadly yes”), caveat (“except for small towns”), example (“in my area the last bus is at 7 p.m.”). That is enough. Do not open a second debate.</p>`,
      points: [
        'Make the caveat about who, where, how much, or what must happen first.',
        'Frames: provided that, as long as, unless, except in, with the caveat that.',
        'One caveat is clearer than a list of six exceptions.',
      ],
      examples: [
        { label: 'Vague caveat', text: 'I agree, but it depends, you know, on many things.' },
        { label: 'Clear caveat', text: 'I support remote work in principle, provided that junior staff still get face-to-face mentoring.' },
      ],
      warning: '“It depends” without a factor is not nuance. Name the factor in the same sentence.',
    },
    {
      title: 'Holding the stance to the end',
      content: `<p>Part 3 follow-up questions try to push you off your position. That is good: you can show flexibility without collapsing. If the examiner says “But what about rural areas?”, do not abandon the city-centre policy. Add a caveat: “That is a fair point — I would not apply the same ban outside large cities.” Your core stance stays; the scope changes.</p>
<p>Signpost the close so the turn does not drift. “So, on balance, I still support it for big cities.” That sentence is the examiner’s note. If you end on a new idea you cannot support, you sound undecided even if the first sentence was clear.</p>
<p>Practise converting absolute answers. Take “I totally agree” and force a rewrite with <em>broadly</em>, <em>in part</em>, or <em>in principle</em>, then add a 10-word caveat. When this is automatic, Part 3 stops feeling like a trap.</p>`,
      points: [
        'Follow-up questions should change the scope, not the entire side.',
        'Close with “on balance…” so the final note matches the opening.',
        'If you truly have no view, invent a limited one: “I have not seen the data, but I would tentatively support a trial.”',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify the nuanced stance',
      type: 'select',
      instructions: 'Choose the option that qualifies the claim without avoiding the question.',
      questions: [
        {
          text: 'A nuanced stance ____.',
          options: ['is all or nothing', 'qualifies the claim', 'avoids the question'],
          answer: 'qualifies the claim',
          explanation: 'Nuance limits how far a claim is true. All-or-nothing is blunt; avoiding the question is vagueness and loses Task Response in speaking.',
        },
        {
          text: 'Which answer takes a side and adds a real limit?',
          options: [
            'I agree in principle, but the evidence is mixed for teenagers.',
            'Maybe it is good or bad, I am not sure about anything.',
            'I completely agree with every part of the statement always.',
          ],
          answer: 'I agree in principle, but the evidence is mixed for teenagers.',
          explanation: 'The first option states support and names a group where the claim is weaker. The others are vague or absolute.',
        },
        {
          text: 'Which follow-up is a caveat rather than a new topic?',
          options: [
            'I would support the ban, provided that night-shift workers still have buses.',
            'Also, education is important and pollution is bad and sport is healthy.',
            'I do not want to talk about transport.',
          ],
          answer: 'I would support the ban, provided that night-shift workers still have buses.',
          explanation: 'A caveat is a condition on the same policy. Listing new topics or refusing the question is not stance control.',
        },
        {
          text: 'What is wrong with “It depends” as a full answer?',
          options: [
            'It names no factor, so the examiner cannot hear a position',
            'It is too formal for IELTS',
            'It is grammatically incorrect',
          ],
          answer: 'It names no factor, so the examiner cannot hear a position',
          explanation: '“It depends” needs an immediate factor — age, place, cost, or timing. Alone it sounds like avoidance.',
        },
      ],
    },
    {
      title: 'Match the move to the meaning',
      type: 'select',
      instructions: 'Choose the qualifier that matches the speaker’s intention.',
      questions: [
        {
          text: 'You like the idea, but you doubt how governments would run it. Best opener?',
          options: ['I agree in principle', 'I have no opinion', 'I totally reject every detail'],
          answer: 'I agree in principle',
          explanation: '“In principle” separates the idea from the implementation — the exact gap you want to talk about.',
        },
        {
          text: 'Only half of the statement is true. Best opener?',
          options: ['I agree in part', 'I broadly and completely agree with all of it', 'Please change the question'],
          answer: 'I agree in part',
          explanation: '“In part” signals a split: you will say which piece you accept and which you reject.',
        },
        {
          text: 'You have heard both sides and still choose one. Best closer?',
          options: ['On balance, I still support it', 'So I agree and disagree equally forever', 'Anyway, whatever'],
          answer: 'On balance, I still support it',
          explanation: '“On balance” shows weighing, then a decision. Equal forever or “whatever” leaves no stance.',
        },
        {
          text: 'Which sentence uses “broadly” correctly?',
          options: [
            'Broadly, I support later start times, though exams might need a new timetable.',
            'Broadly I refuse to answer because the world is complex.',
            'Broadly, I agree and I also completely disagree in the same way.',
          ],
          answer: 'Broadly, I support later start times, though exams might need a new timetable.',
          explanation: '“Broadly” plus a specific limit is the pattern. Using it to refuse or to hold two opposite positions is not nuance.',
        },
      ],
    },
    {
      title: 'Complete the stance frame',
      type: 'fill-in',
      instructions: 'Type the missing word or short phrase from the lesson.',
      questions: [
        {
          text: 'I agree ___ ___, but the evidence is mixed. (idea yes, practice doubtful)',
          answer: 'in principle',
          explanation: '“In principle” is the frame for supporting an idea while questioning how it works in real conditions.',
        },
        {
          text: 'I would support congestion charges, with the ___ that low-income workers get cheaper night buses.',
          answer: 'caveat',
          explanation: 'A caveat is the named condition attached to your support. The noun form is what the prompt asks for.',
        },
        {
          text: '___ ___, I still think city centres should restrict private cars. (after weighing both sides)',
          answer: 'On balance',
          explanation: '“On balance” closes a two-sided discussion with a clear remaining position.',
        },
        {
          text: 'I agree ___ ___ : screens can harm sleep, but they also connect rural students to teachers.',
          answer: 'in part',
          explanation: '“In part” introduces a split claim — one true piece and one limitation — which is what the sentence does.',
        },
      ],
    },
  ],
  tips: [
    'State the side in the first sentence, then add one qualifier — do not hide inside hedges.',
    'A caveat must name who, where, how much, or what happens first.',
    'Use “on balance” to close so the examiner’s last note matches your opening.',
    'Practise the same day: take one absolute opinion and rewrite it with broadly, in part, or in principle.',
  ],
  dailyPractice: [
    'Convert three news-comment opinions from “always/never” into “broadly / in part / with a caveat.”',
    'Answer one Part 3 question in 30 seconds using position + caveat + example.',
    'Record a follow-up: keep your side and only change the scope when the examiner says “What about rural areas?”',
    'Write four frames on a card (broadly, in part, in principle, on balance) and use each once in a day of practice.',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Nuanced Stance speaking course for IELTS. Teaches candidates to qualify opinions with broadly, in part, in principle, to some extent, with caveats, and on balance. Covers concrete caveats, avoiding vagueness, and holding a position when Part 3 follow-up questions change the scope.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...nuancedStanceData,
      title: nuancedStanceData.title + ' (Foundations)',
      overview: { ...nuancedStanceData.overview, duration: '15-20 minutes' },
      sections: nuancedStanceData.sections.slice(0, Math.max(2, Math.ceil(nuancedStanceData.sections.length / 2))),
      exercises: nuancedStanceData.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...nuancedStanceData,
      title: nuancedStanceData.title + ' (Advanced)',
      overview: { ...nuancedStanceData.overview, duration: '30-35 minutes' },
      sections: nuancedStanceData.sections,
      exercises: nuancedStanceData.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(nuancedStanceData.tips || []),
        'For Band 8+: let the example carry the caveat so you do not stack four qualifiers.',
        'Advanced tip: recycle the same stance across two follow-up questions instead of inventing a new opinion each time.',
      ],
    };
  }
  return nuancedStanceData;
}
